import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Generates and downloads Excel from given data
 */
export async function exportToExcel(data, fileName = "data", columns = []) {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Sheet 1");

  // Use provided columns or keys from first object
  const headers = columns.length
    ? columns.map((col) => ({
        header: col.header,
        key: col.accessor,
        width: col.width || 20,
      }))
    : Object.keys(data[0] || {}).map((key) => ({
        header: key.toUpperCase(),
        key,
        width: 20,
      }));

  sheet.columns = headers;

  data.forEach((item) => {
    const row = {};
    headers.forEach((col) => {
      row[col.key] = item[col.key];
    });
    sheet.addRow(row);
  });

  // Style header
  sheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
  sheet.getRow(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "4472C4" },
  };

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `${fileName}.xlsx`);
}

/**
 * Generates the camp's month-wise athlete attendance register.
 * The layout intentionally mirrors the operational workbook used by the camp:
 * athlete details on the left, one column per calendar day, and a daily present
 * count immediately below the athlete rows.
 */
export async function exportMonthlyAttendanceExcel({
  teams = [],
  peopleAttendance = {},
  month,
  fileName,
}) {
  if (!month || !/^\d{4}-\d{2}$/.test(month)) {
    throw new Error("A valid attendance month is required.");
  }

  const [year, monthNumber] = month.split("-").map(Number);
  const daysInMonth = new Date(year, monthNumber, 0).getDate();
  const monthLabel = new Intl.DateTimeFormat("en-IN", {
    month: "long",
    year: "numeric",
  }).format(new Date(year, monthNumber - 1, 1));
  const monthName = new Intl.DateTimeFormat("en-IN", { month: "long" })
    .format(new Date(year, monthNumber - 1, 1));
  const dates = Array.from({ length: daysInMonth }, (_, index) =>
    `${year}-${String(monthNumber).padStart(2, "0")}-${String(index + 1).padStart(2, "0")}`
  );

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Para Sports Association of AP";
  workbook.created = new Date();
  const sheet = workbook.addWorksheet(`${monthName.toUpperCase()} PLAYERS`.slice(0, 31), {
    views: [{ state: "frozen", xSplit: 5, ySplit: 3 }],
    pageSetup: {
      orientation: "landscape",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      paperSize: 9,
      margins: { left: 0.2, right: 0.2, top: 0.3, bottom: 0.3, header: 0.1, footer: 0.1 },
    },
  });

  const totalColumns = 5 + daysInMonth;
  const lastColumn = sheet.getColumn(totalColumns).letter;
  sheet.mergeCells(`A1:${lastColumn}1`);
  sheet.getCell("A1").value = `SSA - Para Sports Association of AP Combined Coaching Camp Attendance Report For the Month of ${monthLabel}`;
  sheet.getCell("A1").font = { name: "Calibri", bold: true, size: 14, color: { argb: "FF000000" } };
  sheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
  sheet.getRow(1).height = 18;

  ["S.No", "Name", "ID No", "District", "Team"].forEach((value, index) => {
    sheet.mergeCells(2, index + 1, 3, index + 1);
    sheet.getCell(2, index + 1).value = value;
  });
  sheet.mergeCells(2, 6, 2, totalColumns);
  sheet.getCell(2, 6).value = "Dates";
  dates.forEach((date, index) => {
    sheet.getCell(3, 6 + index).value = Number(date.slice(-2));
  });

  for (let row = 2; row <= 3; row += 1) {
    for (let col = 1; col <= totalColumns; col += 1) {
      const cell = sheet.getCell(row, col);
      cell.font = { name: "Calibri", bold: true, size: 11, color: { argb: "FF000000" } };
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      cell.border = {
        top: { style: "thin", color: { argb: "FF64748B" } },
        left: { style: "thin", color: { argb: "FF64748B" } },
        bottom: { style: "thin", color: { argb: "FF64748B" } },
        right: { style: "thin", color: { argb: "FF64748B" } },
      };
    }
  }

  const dailyPresent = Object.fromEntries(dates.map((date) => [date, 0]));
  let serial = 1;
  let outputRow = 4;

  const getDailyMark = (attendance = {}) => {
    const statuses = [attendance.morning_status, attendance.evening_status]
      .map((status) => String(status || "").trim().toLowerCase())
      .filter(Boolean);
    if (statuses.includes("present")) return { mark: "✔", present: true };
    if (statuses.includes("late")) return { mark: "Late", present: true };
    if (statuses.includes("permission")) return { mark: "PL", present: false };
    if (statuses.includes("absent")) return { mark: "✘", present: false };
    return { mark: "✘", present: false };
  };

  const teamFills = ["FFFFFF00", "FFB6C7EA", "FFFEDA61", "FF7EDFD7", "FFFFFFFF"];
  const athletesById = new Map();
  teams.forEach((team, teamIndex) => {
    const days = Array.isArray(team.days) ? team.days : [];
    days.forEach((day) => {
      (day.athletes || []).forEach((athlete) => {
        const athleteId = athlete.athlete_id ?? athlete.id ?? athlete.id_number ?? athlete.bib_no ?? athlete.athlete_name;
        const existingAthlete = athletesById.get(athleteId);
        const isCurrentTeam = !existingAthlete || day.date >= existingAthlete.currentTeamDate;
        const mergedAthlete = existingAthlete || { attendanceByDate: {} };

        if (isCurrentTeam) {
          Object.assign(mergedAthlete, athlete, {
            attendanceByDate: mergedAthlete.attendanceByDate,
          });
        }
        mergedAthlete.attendanceByDate[day.date] = athlete;
        if (isCurrentTeam) {
          mergedAthlete.currentTeamIndex = teamIndex;
          mergedAthlete.currentTeamDate = day.date;
        }
        athletesById.set(athleteId, mergedAthlete);
      });
    });
  });

  teams.forEach((team, teamIndex) => {
    const teamFill = teamFills[teamIndex % teamFills.length];
    const teamAthletes = Array.from(athletesById.values())
      .filter((athlete) => athlete.currentTeamIndex === teamIndex);

    const teamStartRow = outputRow;
    teamAthletes.forEach((athlete) => {
      const removedInExportMonth = Boolean(athlete.removed_in_export_month);
      const lastPresentDate = removedInExportMonth
        ? dates.reduce((latest, date) => getDailyMark(athlete.attendanceByDate[date]).present ? date : latest, "")
        : "";
      const row = sheet.getRow(outputRow);
      row.values = [
        serial,
        athlete.athlete_name || athlete.name || "",
        athlete.id_number || athlete.bib_no || "",
        athlete.district || "",
        team.team_name || "General Squad",
        ...dates.map((date) => {
          const result = getDailyMark(athlete.attendanceByDate[date]);
          if (result.present) dailyPresent[date] += 1;
          if (removedInExportMonth && !result.mark && (!lastPresentDate || date > lastPresentDate)) {
            return "✘";
          }
          return result.mark;
        }),
      ];
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        cell.font = { name: colNumber === 2 || colNumber === 5 ? "Aptos Display" : "Calibri", size: 11 };
        cell.alignment = {
          horizontal: colNumber === 2 || colNumber === 4 ? "left" : "center",
          vertical: "middle",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin", color: { argb: "FF94A3B8" } },
          left: { style: "thin", color: { argb: "FF94A3B8" } },
          bottom: { style: "thin", color: { argb: "FF94A3B8" } },
          right: { style: "thin", color: { argb: "FF94A3B8" } },
        };
        if (colNumber <= 5) {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: teamFill } };
        }
        if (colNumber >= 6) {
          if (cell.value === "✔") cell.font = { name: "Calibri", size: 11, color: { argb: "FF000000" } };
          if (cell.value === "✘") cell.font = { name: "Calibri", size: 11, color: { argb: "FF000000" } };
          if (cell.value === "Late" || cell.value === "PL") cell.font = { name: "Calibri", size: 10, color: { argb: "FF000000" } };
        }
      });
      serial += 1;
      outputRow += 1;
    });

    if (outputRow > teamStartRow) {
      if (outputRow - teamStartRow > 1) {
        sheet.mergeCells(teamStartRow, 5, outputRow - 1, 5);
      }
      const teamCell = sheet.getCell(teamStartRow, 5);
      teamCell.value = team.team_name || "General Squad";
      teamCell.font = { name: "Aptos Display", size: 11, color: { argb: "FF000000" } };
      teamCell.alignment = { horizontal: "center", vertical: "middle", textRotation: 0, wrapText: true };
      teamCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: teamFill } };
    }
  });

  sheet.mergeCells(outputRow, 1, outputRow, 5);
  const summaryCell = sheet.getCell(outputRow, 1);
  summaryCell.value = "PRESENT STUDENTS";
  summaryCell.font = { bold: true, size: 10, color: { argb: "FFFFFFFF" } };
  summaryCell.alignment = { horizontal: "right", vertical: "middle" };
  dates.forEach((date, index) => {
    const cell = sheet.getCell(outputRow, 6 + index);
    cell.value = dailyPresent[date];
    cell.font = { bold: true, size: 10, color: { argb: "FFFFFFFF" } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
  });
  for (let col = 1; col <= totalColumns; col += 1) {
    const cell = sheet.getCell(outputRow, col);
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF047857" } };
    cell.border = {
      top: { style: "medium", color: { argb: "FF065F46" } },
      left: { style: "thin", color: { argb: "FFFFFFFF" } },
      bottom: { style: "medium", color: { argb: "FF065F46" } },
      right: { style: "thin", color: { argb: "FFFFFFFF" } },
    };
  }
  sheet.getRow(outputRow).height = 24;

  sheet.getColumn(1).width = 6.1111111111111;
  sheet.getColumn(2).width = 19.777777777778;
  sheet.getColumn(3).width = 7.1111111111111;
  sheet.getColumn(4).width = 16.444444444444;
  sheet.getColumn(5).width = 16.444444444444;
  for (let col = 6; col <= totalColumns; col += 1) sheet.getColumn(col).width = 4.5555555555556;
  sheet.headerFooter.oddFooter = "&L ✔ = Present   ✘ = Absent   Late = Present but late   PL = Permission&CPage &P of &N&RGenerated &D";

  const addPeopleAttendanceSheet = (label, rows = [], detailHeader = "Role / Details", nameHeader = "Name", summaryLabel = "PRESENT PERSONS") => {
    const peopleSheet = workbook.addWorksheet(`${monthName.toUpperCase()} ${label}`.slice(0, 31), {
      views: [{ state: "frozen", xSplit: 4, ySplit: 3 }],
      pageSetup: {
        orientation: "landscape", fitToPage: true, fitToWidth: 1, fitToHeight: 0, paperSize: 9,
        margins: { left: 0.2, right: 0.2, top: 0.3, bottom: 0.3, header: 0.1, footer: 0.1 },
      },
    });
    const peopleTotalColumns = 4 + daysInMonth;
    const peopleLastColumn = peopleSheet.getColumn(peopleTotalColumns).letter;
    peopleSheet.mergeCells(`A1:${peopleLastColumn}1`);
    peopleSheet.getCell("A1").value = `SSA - Para Sports Association of AP ${label} Attendance Report For the Month of ${monthLabel}`;
    peopleSheet.getCell("A1").font = { name: "Calibri", bold: true, size: 14 };
    peopleSheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };

    ["S.No", nameHeader, "Mobile", detailHeader].forEach((value, index) => {
      peopleSheet.mergeCells(2, index + 1, 3, index + 1);
      peopleSheet.getCell(2, index + 1).value = value;
    });
    peopleSheet.mergeCells(2, 5, 2, peopleTotalColumns);
    peopleSheet.getCell(2, 5).value = "Dates";
    dates.forEach((date, index) => { peopleSheet.getCell(3, 5 + index).value = Number(date.slice(-2)); });

    for (let row = 2; row <= 3; row += 1) {
      for (let col = 1; col <= peopleTotalColumns; col += 1) {
        const cell = peopleSheet.getCell(row, col);
        cell.font = { name: "Calibri", bold: true, size: 10 };
        cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9EAF7" } };
        cell.border = {
          top: { style: "thin", color: { argb: "FF64748B" } }, left: { style: "thin", color: { argb: "FF64748B" } },
          bottom: { style: "thin", color: { argb: "FF64748B" } }, right: { style: "thin", color: { argb: "FF64748B" } },
        };
      }
    }

    const dailyCounts = Object.fromEntries(dates.map((date) => [date, 0]));
    const markFor = (attendance = {}) => {
      const statuses = [attendance.morning_status, attendance.evening_status]
        .map((status) => String(status || "").trim().toLowerCase()).filter(Boolean);
      if (statuses.includes("present")) return { mark: "P", present: true };
      if (statuses.includes("late")) return { mark: "Late", present: true };
      if (statuses.includes("on_duty")) return { mark: "OD", present: true };
      if (statuses.includes("permission")) return { mark: "PL", present: false };
      if (statuses.includes("absent")) return { mark: "✘", present: false };
      return { mark: "✘", present: false };
    };

    let peopleRow = 4;
    rows.forEach((person, index) => {
      const row = peopleSheet.getRow(peopleRow);
      row.values = [index + 1, person.name || "", person.mobile || "", person.detail || "", ...dates.map((date) => {
        const result = markFor(person.attendance?.[date]);
        if (result.present) dailyCounts[date] += 1;
        return result.mark;
      })];
      row.eachCell({ includeEmpty: true }, (cell, column) => {
        cell.font = { name: "Calibri", size: 10 };
        cell.alignment = { horizontal: [2, 4].includes(column) ? "left" : "center", vertical: "middle", wrapText: true };
        cell.border = {
          top: { style: "thin", color: { argb: "FF94A3B8" } }, left: { style: "thin", color: { argb: "FF94A3B8" } },
          bottom: { style: "thin", color: { argb: "FF94A3B8" } }, right: { style: "thin", color: { argb: "FF94A3B8" } },
        };
      });
      peopleRow += 1;
    });

    peopleSheet.mergeCells(peopleRow, 1, peopleRow, 4);
    peopleSheet.getCell(peopleRow, 1).value = summaryLabel;
    for (let col = 1; col <= peopleTotalColumns; col += 1) {
      const cell = peopleSheet.getCell(peopleRow, col);
      if (col >= 5) cell.value = dailyCounts[dates[col - 5]];
      cell.font = { name: "Calibri", bold: true, size: 10, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF047857" } };
      cell.alignment = { horizontal: col === 1 ? "right" : "center", vertical: "middle" };
      cell.border = {
        top: { style: "thin", color: { argb: "FFFFFFFF" } }, left: { style: "thin", color: { argb: "FFFFFFFF" } },
        bottom: { style: "thin", color: { argb: "FFFFFFFF" } }, right: { style: "thin", color: { argb: "FFFFFFFF" } },
      };
    }
    peopleSheet.getColumn(1).width = 6;
    peopleSheet.getColumn(2).width = 22;
    peopleSheet.getColumn(3).width = 14;
    peopleSheet.getColumn(4).width = 20;
    for (let col = 5; col <= peopleTotalColumns; col += 1) peopleSheet.getColumn(col).width = 4.5;
    peopleSheet.headerFooter.oddFooter = "&LP = Present   ✘ = Absent   Late = Present but late   OD = On Duty   PL = Permission&CPage &P of &N&RGenerated &D";
  };

  addPeopleAttendanceSheet("PARA OFFICIALS", peopleAttendance.para_officials || [], "Team / Designation");
  addPeopleAttendanceSheet("SSA OFFICIALS", peopleAttendance.ssa_officials || [], "Designation");
  addPeopleAttendanceSheet("IRPS", peopleAttendance.irps || [], "District");
  addPeopleAttendanceSheet("ATTENDANTS", peopleAttendance.attendants || [], "Student ID", "Student Name", "PRESENT ATTENDANTS");

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `${fileName || `Monthly_Attendance_${month}`}.xlsx`);
}

/**
 * Generates and downloads PDF from given data
 */
export function exportToPDF(data, fileName = "data", columns = []) {
  const doc = new jsPDF({ orientation: "landscape" });

  const headers = columns.length
    ? columns.map((col) => col.header)
    : Object.keys(data[0] || {});
  const body = data.map((row) =>
    columns.length
      ? columns.map((col) => row[col.accessor])
      : Object.values(row)
  );

  doc.setFontSize(14);
  doc.text(fileName.toUpperCase(), 14, 16);

  autoTable(doc, {
    startY: 20,
    head: [headers],
    body,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [68, 114, 196] },
  });

  doc.save(`${fileName}.pdf`);
}
